import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Briefcase, Clock, Palmtree, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../mock';

const Services = () => {
  const iconMap = {
    plane: Plane,
    briefcase: Briefcase,
    clock: Clock,
    palmtree: Palmtree
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Nos Services
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Des prestations sur-mesure pour répondre à tous vos besoins de transport sur la Côte d'Azur
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {mockData.services.map((service, idx) => {
              const IconComponent = iconMap[service.icon];
              const isEven = idx % 2 === 0;
              
              return (
                <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  !isEven ? 'lg:grid-flow-dense' : ''
                }`}>
                  <div className={isEven ? '' : 'lg:col-start-2'}>
                    <Card className="bg-slate-900 border-slate-800 overflow-hidden group hover:border-amber-600/50 transition-all duration-300">
                      <CardContent className="p-0">
                        <div className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center group-hover:from-amber-900/20 group-hover:to-slate-900 transition-all duration-300">
                          {IconComponent && <IconComponent className="w-24 h-24 text-amber-500" />}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className={`space-y-6 ${!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                    <div className="inline-block px-4 py-1 bg-amber-600/10 border border-amber-600/30 rounded-full">
                      <span className="text-amber-500 text-sm font-medium">Service {service.id}</span>
                    </div>
                    
                    <h2 className="text-4xl font-bold text-white">
                      {service.title}
                    </h2>
                    
                    <p className="text-gray-400 text-lg leading-relaxed">
                      {service.description}
                    </p>

                    <div className="space-y-3">
                      <h3 className="text-xl font-semibold text-white">Inclus dans ce service :</h3>
                      <ul className="space-y-3">
                        {service.features.map((feature) => (
                          <li key={feature} className="flex items-center space-x-3">
                            <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link to="/reservation">
                      <Button className="bg-amber-600 hover:bg-amber-700 text-white transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50" data-testid={`service-reserve-btn-${service.id}`}>
                        <Phone className="w-4 h-4 mr-2" />
                        Réserver ce service
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Avantages AzurDriver Pro
            </h2>
            <p className="text-gray-400 text-lg">
              Tous nos services incluent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Véhicules Premium', description: 'Flotte récente et entretenue' },
              { title: 'Chauffeurs Professionnels', description: 'Expérimentés et courtois' },
              { title: 'Assurance Tous Risques', description: 'Pour votre sécurité' },
              { title: 'WiFi à Bord', description: 'Restez connecté' },
              { title: 'Bouteilles d\'Eau', description: 'Rafraîchissements offerts' },
              { title: 'Paiement Flexible', description: 'CB, espèces, virement' }
            ].map((benefit) => (
              <Card key={benefit.title} className="bg-slate-950 border-slate-800 hover:border-amber-600/50 transition-all duration-300">
                <CardContent className="p-6 text-center space-y-2">
                  <CheckCircle className="w-8 h-8 text-amber-500 mx-auto" />
                  <h3 className="text-lg font-semibold text-white">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Besoin d'un Service Personnalisé ?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Contactez-nous pour discuter de vos besoins spécifiques
          </p>
          <Link to="/contact">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 transition-all duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Nous Contacter
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
